import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, User, LogOut, Ship, Settings, ExternalLink, Shield, Sparkles } from 'lucide-react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { motion, AnimatePresence } from 'framer-motion';
import { hapticFeedback } from '@/hooks/useHapticFeedback';
import { cn } from '@/lib/utils';

interface SubMenuItem {
  name: string;
  href?: string;
  isBold?: boolean;
  external?: boolean;
  submenu?: SubMenuItem[];
}

interface NavigationItem {
  name: string;
  href?: string;
  submenu?: SubMenuItem[];
}

interface MobileSheetMenuProps {
  isOpen: boolean;
  onClose: () => void;
  navigationItems: NavigationItem[];
  isAuthenticated: boolean;
  user?: { email?: string } | null;
  profile?: { first_name?: string; last_name?: string } | null;
  onSignOut: () => void;
  isAdmin?: boolean;
}

export const MobileSheetMenu = ({
  isOpen,
  onClose,
  navigationItems,
  isAuthenticated,
  user,
  profile,
  onSignOut,
  isAdmin,
}: MobileSheetMenuProps) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const isActive = useCallback((href?: string) => href ? location.pathname === href : false, [location.pathname]);

  const toggleExpanded = useCallback((name: string) => {
    hapticFeedback('light');
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  }, []);

  const handleLinkClick = useCallback(() => {
    hapticFeedback('selection');
    onClose();
  }, [onClose]);

  const userInitials = profile?.first_name && profile?.last_name
    ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || 'U';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        hideCloseButton
        className={cn(
          "w-[85vw] max-w-[340px] p-0",
          "bg-background/95 backdrop-blur-xl",
          "border-l border-border/30"
        )}
      >
        {/* Header with user info */}
        <div className="px-5 pt-6 pb-4 border-b border-border/30">
          {isAuthenticated ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12 ring-2 ring-primary/20">
                <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                {profile?.first_name && (
                  <p className="font-semibold text-foreground truncate">
                    {profile.first_name} {profile.last_name}
                  </p>
                )}
                <p className="text-sm text-muted-foreground truncate">
                  {user?.email}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-foreground">Menu</span>
              <Button 
                asChild 
                size="sm"
                className="bg-primary hover:bg-primary/90 h-9"
              >
                <Link to="/auth" onClick={handleLinkClick}>
                  <User className="h-4 w-4 mr-1.5" />
                  Sign In
                </Link>
              </Button>
            </div>
          )}
        </div>

        {/* Navigation items */}
        <div className="flex-1 overflow-y-auto py-2">
          <nav className="px-3">
            {navigationItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.03, duration: 0.2 }}
              >
                {item.submenu ? (
                  <Collapsible
                    open={expandedItems.has(item.name)}
                    onOpenChange={() => toggleExpanded(item.name)}
                  >
                    <CollapsibleTrigger asChild>
                      <button
                        className={cn(
                          "flex items-center justify-between w-full px-3 py-3.5 rounded-xl",
                          "text-left font-medium text-foreground",
                          "transition-colors duration-150",
                          "hover:bg-muted/60 active:bg-muted"
                        )}
                      >
                        <span>{item.name}</span>
                        <motion.div
                          animate={{ rotate: expandedItems.has(item.name) ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </motion.div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                        className="ml-3 pl-3 border-l-2 border-border/50 space-y-0.5 pb-2"
                      >
                        {item.submenu.map((subItem) => (
                          subItem.submenu ? (
                            <NestedSubmenu
                              key={subItem.name}
                              item={subItem}
                              onLinkClick={handleLinkClick}
                              isActive={isActive}
                            />
                          ) : (
                            <SubMenuLink
                              key={subItem.name}
                              item={subItem}
                              onLinkClick={handleLinkClick}
                              isActive={isActive(subItem.href)}
                            />
                          )
                        ))}
                      </motion.div>
                    </CollapsibleContent>
                  </Collapsible>
                ) : item.href ? (
                  <Link
                    to={item.href}
                    onClick={handleLinkClick}
                    className={cn(
                      "flex items-center px-3 py-3.5 rounded-xl",
                      "font-medium transition-colors duration-150",
                      "hover:bg-muted/60 active:bg-muted",
                      isActive(item.href) 
                        ? "text-primary bg-primary/5" 
                        : "text-foreground"
                    )}
                  >
                    {item.name}
                  </Link>
                ) : null}
              </motion.div>
            ))}
          </nav>
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-border/30 space-y-2">
          <Button 
            asChild 
            className="w-full bg-primary hover:bg-primary/90 h-12 text-base font-semibold"
          >
            <Link to="/valuation" onClick={handleLinkClick}>
              Get Your Boat Value
            </Link>
          </Button>
          
          <Button 
            asChild 
            variant="outline" 
            className="w-full h-11 border-secondary text-secondary hover:bg-secondary/10"
          >
            <Link to="/upgrade" onClick={handleLinkClick}>
              <Sparkles className="h-4 w-4 mr-2" />
              Upgrade
            </Link>
          </Button>
          
          {isAdmin && (
            <Button 
              asChild 
              variant="outline" 
              className="w-full h-11"
            >
              <Link to="/admin" onClick={handleLinkClick}>
                <Shield className="h-4 w-4 mr-2" />
                Admin Dashboard
              </Link>
            </Button>
          )}

          {isAuthenticated && (
            <>
              <Button 
                asChild 
                variant="outline" 
                className="w-full h-11"
              >
                <Link to="/my-portfolio" onClick={handleLinkClick}>
                  <Ship className="h-4 w-4 mr-2" />
                  My Command Center
                </Link>
              </Button>
              <Button 
                asChild 
                variant="ghost" 
                className="w-full h-11"
              >
                <Link to="/account-settings" onClick={handleLinkClick}>
                  <Settings className="h-4 w-4 mr-2" />
                  Account Settings
                </Link>
              </Button>
              <Button 
                variant="ghost" 
                className="w-full h-11 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => {
                  hapticFeedback('medium');
                  onSignOut();
                  onClose();
                }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign Out
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Nested submenu component
const NestedSubmenu = ({
  item,
  onLinkClick,
  isActive,
}: {
  item: SubMenuItem;
  onLinkClick: () => void;
  isActive: (href?: string) => boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          className={cn(
            "flex items-center justify-between w-full px-3 py-2.5 rounded-lg",
            "text-left text-sm font-medium text-foreground/80",
            "transition-colors duration-150",
            "hover:bg-muted/40 active:bg-muted/60"
          )}
          onClick={() => hapticFeedback('light')}
        >
          <span>{item.name}</span>
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.15 }}
          >
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </motion.div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-2 pl-2 border-l border-border/30 space-y-0.5 py-1">
          {item.submenu?.map((nestedItem) => (
            <SubMenuLink
              key={nestedItem.name}
              item={nestedItem}
              onLinkClick={onLinkClick}
              isActive={isActive(nestedItem.href)}
              isNested
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

// Submenu link component
const SubMenuLink = ({
  item,
  onLinkClick,
  isActive,
  isNested = false,
}: {
  item: SubMenuItem;
  onLinkClick: () => void;
  isActive: boolean;
  isNested?: boolean;
}) => {
  if (!item.href) return null;
  
  const baseClasses = cn(
    "flex items-center gap-2 px-3 rounded-lg transition-colors duration-150",
    "hover:bg-muted/40 active:bg-muted/60",
    isNested ? "py-2 text-xs" : "py-2.5 text-sm",
    item.isBold ? "font-semibold text-foreground" : "text-muted-foreground",
    isActive && "text-primary bg-primary/5"
  );

  if (item.external) {
    return (
      <a
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onLinkClick}
        className={baseClasses}
      >
        <span className="flex-1">{item.name}</span>
        <ExternalLink className="h-3 w-3 opacity-50" />
      </a>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onLinkClick}
      className={baseClasses}
    >
      {item.name}
    </Link>
  );
};

export default MobileSheetMenu;
